/**
 * TypeScript type definitions for PracticeMD cryptographic operations
 *
 * These types mirror the Go implementations in:
 * - /common/crypto/keys.go
 * - /common/encoding/encoding.go
 */

// ============================================================================
// Encryption Types
// ============================================================================

/**
 * EncryptOutput contains encrypted data and associated metadata
 *
 * Corresponds to Go: common/crypto/keys.go EncryptOutput struct
 *
 * Supports two encryption algorithms:
 * 1. "AES-256-GCM" - Symmetric encryption with AES
 * 2. "x25519-nacl-box" - Asymmetric encryption with X25519 + NaCl box
 */
export interface EncryptOutput {
  /** RSA public key ID (legacy, rarely used) */
  publicKeyId?: string;

  /** Encrypted data key ciphertext (base64) - For RSA encryption */
  dataKeyCiphertext?: string;

  /** Encrypted data (base64) */
  ciphertext: string;

  /** ID of the data key used (hex-encoded SHA-256) */
  dataKeyId?: string;

  /** Encryption algorithm: "AES-256-GCM" or "x25519-nacl-box" */
  algorithm: string;

  /** Key algorithm (e.g., "RSA-OAEP-256") */
  keyAlgorithm?: string;

  /** X25519 sender public key (base64) - For NaCl box */
  senderPublicKey?: string;

  /** X25519 recipient public key (base64) - For NaCl box */
  recipientPublicKey?: string;

  /** Nonce for encryption (base64) - 24 bytes for NaCl box, 12 bytes for AES-GCM */
  nonce?: string;
}


// ============================================================================
// Signing Types
// ============================================================================



// ============================================================================
// Keyring Types
// ============================================================================

/**
 * KeyId is a string identifier for keys
 */
export type KeyId = string;

/**
 * ChainLinkId is a string identifier for chain links
 */
export type ChainLinkId = string;

/**
 * KeyInfo contains encrypted key material
 *
 * Corresponds to Go: common/crypto/keys.go KeyInfo struct
 */
export interface KeyInfo {
  /** Base64-encoded key bytes */
  key: string;
}

/**
 * ChainLink represents a link in a keychain
 *
 * Corresponds to Go: common/crypto/keys.go ChainLink struct
 */
export interface ChainLink {
  /** Key ID for this chain link */
  keyId: KeyId;

  /** Metadata for this chain link */
  metadata: Record<string, string>;

  /** Previous chain link ID (if any) */
  previous?: ChainLinkId;
}

/**
 * Keyring manages multiple keychains and keys
 *
 * Corresponds to Go: common/crypto/keys.go Keyring struct
 *
 * @deprecated Use SecureKeyring instead for better security
 */
export interface Keyring {
  /** Map of keychain alias to latest chain link ID */
  keychains: Record<string, ChainLinkId>;

  /** Map of key ID to key info (encrypted key material) */
  keys: Record<KeyId, KeyInfo>;

  /** Map of chain link ID to chain link */
  chainlinks: Record<ChainLinkId, ChainLink>;
}


// ============================================================================
// Utility Types
// ============================================================================

/**
 * HKDF kind determines the key derivation info string
 */
export type HKDFKind = 'device' | 'puk' | 'ptk' | 'content' | 'ephemeral';

/**
 * Key type for HKDF derivation
 */
export type KeyType = 'eddsa' | 'x25519' | 'aes';






// ============================================================================
// Error Types
// ============================================================================

/**
 * CryptoError for crypto operation failures
 */
export class CryptoError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'CryptoError';
  }
}
